// src/components/EditPendentLoad.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import axios from "../axiosConfig";

import {
  getSocket,
} from "../lib/socket";

import {
  readShipmentPriceNegotiationCache,
  subscribeShipmentPriceNegotiationCache,
  writeShipmentPriceNegotiationCache,
} from "../utils/shipmentPriceNegotiationCache.js";

import PendentLoadWorkspace from "./pendent-load/PendentLoadWorkspace.jsx";

import "../styles/EditPendentLoad.css";
import "../styles/EditShipmentRequestPage.css";
import "../styles/EditLoadWorkspace.css";
import "../styles/EditPendentLoadWorkspace.css";

const MAX_POINTS =
  7;

const NORMAL_MAX = {
  length:
    13.6,

  height:
    2.7,

  width:
    2.5,
};

const SPECIAL_MAX = {
  length:
    20,

  height:
    5,

  width:
    4,
};

const SPECIAL_THRESHOLD = {
  length:
    13.61,

  height:
    2.71,

  width:
    2.51,
};

function makeEmptyPoint() {
  return {
    location:
      "",

    date:
      "",

    hour:
      "",

    length:
      "",

    width:
      "",

    height:
      "",

    lengthError:
      "",

    widthError:
      "",

    heightError:
      "",

    placeId:
      null,

    address:
      "",

    postalCode:
      "",

    city:
      "",

    country:
      "",

    gMapsLink:
      "",

    coordinates:
      "",

    latitude:
      null,

    longitude:
      null,

    contactPhone:
      "",

    reference:
      "",

    workingHours:
      null,

    loadingAccess:
      null,

    loadingAccessError:
      false,

    handlingEquipment:
      null,

    handlingEquipmentOther:
      "",

    handlingEquipmentError:
      false,

    handlingEquipmentOtherError:
      false,

    showMoreFields:
      false,

    locationError:
      false,

    dateError:
      false,

    hourError:
      false,

    lengthErrorFlag:
      false,

    widthErrorFlag:
      false,

    heightErrorFlag:
      false,

    addressError:
      false,

    postalCodeError:
      false,

    cityError:
      false,

    countryError:
      false,

    gMapsLinkError:
      false,

    coordinatesError:
      false,

    contactPhoneError:
      false,
  };
}

function normalizePoint(
  point = {}
) {
  return {
    ...makeEmptyPoint(),

    ...point,

    length:
      point.length ===
        null ||
      point.length ===
        undefined
        ? ""
        : String(
            point.length
          ),

    width:
      point.width ===
        null ||
      point.width ===
        undefined
        ? ""
        : String(
            point.width
          ),

    height:
      point.height ===
        null ||
      point.height ===
        undefined
        ? ""
        : String(
            point.height
          ),

    reference:
      typeof point.reference ===
      "string"
        ? point.reference
        : "",

    workingHours:
      point.workingHours ??
      null,

    loadingAccess:
      point.loadingAccess ??
      null,

    handlingEquipment:
      point.handlingEquipment ??
      null,

    handlingEquipmentOther:
      point.handlingEquipmentOther ??
      "",

    showMoreFields:
      Boolean(
        point.showMoreFields
      ),

    locationError:
      false,

    dateError:
      false,

    hourError:
      false,

    lengthError:
      "",

    widthError:
      "",

    heightError:
      "",

    lengthErrorFlag:
      false,

    widthErrorFlag:
      false,

    heightErrorFlag:
      false,

    loadingAccessError:
      false,

    handlingEquipmentError:
      false,

    handlingEquipmentOtherError:
      false,

    addressError:
      false,

    postalCodeError:
      false,

    cityError:
      false,

    countryError:
      false,

    gMapsLinkError:
      false,

    coordinatesError:
      false,

    contactPhoneError:
      false,
  };
}

function pickFirstArray(
  ...candidates
) {
  for (
    const candidate of
    candidates
  ) {
    if (
      Array.isArray(
        candidate
      ) &&
      candidate.length >
        0
    ) {
      return candidate;
    }
  }

  return [];
}

function getPrefillPickupPoints(
  prefill
) {
  return pickFirstArray(
    prefill?.pickupDeliveries,
    prefill?.pickupPoints,
    prefill?.pickupLocations
  );
}

function getPrefillDeliveryPoints(
  prefill
) {
  return pickFirstArray(
    prefill?.deliveryDeliveries,
    prefill?.deliveryPoints,
    prefill?.deliveryLocations
  );
}

function normalizeEmailOptions(
  values
) {
  if (
    !Array.isArray(
      values
    )
  ) {
    return [];
  }

  return values
    .map(
      (
        value
      ) =>
        String(
          value ||
            ""
        )
          .trim()
          .toLowerCase()
    )
    .filter(
      Boolean
    )
    .map(
      (
        email
      ) => ({
        value:
          email,

        label:
          email,
      })
    );
}

function normalizeCarrierPriceProposal(
  proposal
) {
  if (
    !proposal ||
    proposal.id ===
      null ||
    proposal.id ===
      undefined
  ) {
    return null;
  }

  return {
    id:
      Number(
        proposal.id
      ),

    requestId:
      Number(
        proposal.requestId
      ),

    requestConversationId:
      Number(
        proposal
          .requestConversationId
      ),

    shipperCompanyId:
      Number(
        proposal
          .shipperCompanyId
      ),

    carrierCompanyId:
      Number(
        proposal
          .carrierCompanyId
      ),

    parentProposalId:
      proposal
        .parentProposalId ===
        null ||
      proposal
        .parentProposalId ===
        undefined
        ? null
        : Number(
            proposal
              .parentProposalId
          ),

    proposedByType:
      String(
        proposal
          .proposedByType ||
          ""
      )
        .trim()
        .toUpperCase(),

    amount:
      proposal.amount,

    currency:
      String(
        proposal.currency ||
          "EUR"
      )
        .trim()
        .toUpperCase(),

    status:
      String(
        proposal.status ||
          ""
      )
        .trim()
        .toUpperCase(),

    note:
      proposal.note ||
      "",

    commercialTerms:
      proposal
        .commercialTerms &&
      typeof proposal
        .commercialTerms ===
        "object"
        ? proposal
            .commercialTerms
        : {},

    validUntil:
      proposal
        .validUntil ||
      null,

    respondedAt:
      proposal
        .respondedAt ||
      null,

    acceptedAt:
      proposal
        .acceptedAt ||
      null,

    withdrawnAt:
      proposal
        .withdrawnAt ||
      null,

    createdAt:
      proposal
        .createdAt ||
      null,

    updatedAt:
      proposal
        .updatedAt ||
      null,
  };
}

function normalizeCarrierPriceNegotiation(
  payload = {}
) {
  const proposals =
    (
      Array.isArray(
        payload?.proposals
      )
        ? payload.proposals
        : []
    )
      .map(
        normalizeCarrierPriceProposal
      )
      .filter(
        Boolean
      );

  const latestProposalId =
    Number(
      payload
        ?.conversation
        ?.latestPriceProposalId
    );

  const latestProposal =
    proposals.find(
      (
        proposal
      ) =>
        Number(
          proposal.id
        ) ===
        latestProposalId
    ) ||
    normalizeCarrierPriceProposal(
      payload
        ?.conversation
        ?.latestPriceProposal
    ) ||
    proposals[
      proposals.length -
        1
    ] ||
    null;

  return {
    conversation:
      payload?.conversation ||
      null,

    proposals,

    latestProposal,

    actions:
      payload?.actions &&
      typeof payload.actions ===
        "object"
        ? payload.actions
        : {},
  };
}

function calculatePointDimensionTotals(
  points = []
) {
  return points.reduce(
    (
      accumulator,
      point
    ) => {
      const pointLength =
        Number(
          point?.length ||
            0
        );

      const pointWidth =
        Number(
          point?.width ||
            0
        );

      const pointHeight =
        Number(
          point?.height ||
            0
        );

      return {
        length:
          accumulator.length +
          (
            Number.isFinite(
              pointLength
            )
              ? pointLength
              : 0
          ),

        width:
          Math.max(
            accumulator.width,

            Number.isFinite(
              pointWidth
            )
              ? pointWidth
              : 0
          ),

        height:
          Math.max(
            accumulator.height,

            Number.isFinite(
              pointHeight
            )
              ? pointHeight
              : 0
          ),
      };
    },
    {
      length:
        0,

      width:
        0,

      height:
        0,
    }
  );
}

function sanitizePoints(
  points = []
) {
  return points.map(
    (
      point
    ) => {
      const normalizedHandlingEquipment =
        point.handlingEquipment
          ? String(
              point.handlingEquipment
            )
              .trim()
              .toUpperCase()
          : null;

      const normalizedHandlingEquipmentOther =
        normalizedHandlingEquipment ===
        "OTHER"
          ? String(
              point.handlingEquipmentOther ||
                ""
            ).trim() ||
            null
          : null;

      const latitude =
        Number(
          point.latitude ??
            point.lat
        );

      const longitude =
        Number(
          point.longitude ??
            point.lng ??
            point.lon
        );

      return {
        placeId:
          point.placeId ??
          null,

        location:
          String(
            point.location ||
              ""
          ).trim(),

        date:
          point.date ||
          null,

        hour:
          point.hour ||
          null,

        length:
          point.length ===
            "" ||
          point.length ===
            null ||
          point.length ===
            undefined
            ? null
            : Number(
                point.length
              ),

        width:
          point.width ===
            "" ||
          point.width ===
            null ||
          point.width ===
            undefined
            ? null
            : Number(
                point.width
              ),

        height:
          point.height ===
            "" ||
          point.height ===
            null ||
          point.height ===
            undefined
            ? null
            : Number(
                point.height
              ),

        address:
          String(
            point.address ||
              ""
          ).trim() ||
          null,

        postalCode:
          String(
            point.postalCode ||
              ""
          ).trim() ||
          null,

        city:
          String(
            point.city ||
              ""
          ).trim() ||
          null,

        country:
          String(
            point.country ||
              ""
          ).trim() ||
          null,

        gMapsLink:
          String(
            point.gMapsLink ||
              ""
          ).trim() ||
          null,

        coordinates:
          String(
            point.coordinates ||
              ""
          ).trim() ||
          null,

        latitude:
          Number.isFinite(
            latitude
          )
            ? latitude
            : null,

        longitude:
          Number.isFinite(
            longitude
          )
            ? longitude
            : null,

        contactPhone:
          String(
            point.contactPhone ||
              ""
          ).trim() ||
          null,

        reference:
          String(
            point.reference ||
              ""
          ).trim() ||
          null,

        workingHours:
          point.workingHours ??
          null,

        loadingAccess:
          point.loadingAccess
            ? String(
                point.loadingAccess
              )
                .trim()
                .toUpperCase()
            : null,

        handlingEquipment:
          normalizedHandlingEquipment,

        handlingEquipmentOther:
          normalizedHandlingEquipmentOther,
      };
    }
  );
}

const EditPendentLoad =
  () => {
    const {
      id,
    } =
      useParams();

    const navigate =
      useNavigate();

    const location =
      useLocation();

    const {
        t,
      } =
        useTranslation([
          "edit_pendentload",
          "edit_load",
        ]);

    const prefill =
      location.state?.prefill ??
      null;

    const sourceType =
      String(
        location.state?.sourceType ||
          prefill?.sourceType ||
          ""
      )
        .trim()
        .toUpperCase();

    const isShipmentRequest =
      sourceType ===
        "SHIPPER_REQUEST" ||
      new URLSearchParams(
        location.search
      ).get(
        "source"
      ) ===
        "shipper-request";

    const shipmentRequestId =
      (() => {
        if (
          !isShipmentRequest
        ) {
          return null;
        }

        const numericRequestId =
          Number(
            location.state
              ?.requestId ||
            prefill
              ?.requestId ||
            id
          );

        return (
          Number.isInteger(
            numericRequestId
          ) &&
          numericRequestId >
            0
        )
          ? numericRequestId
          : null;
      })();

    const queryParameters =
      new URLSearchParams(
        location.search
      );

    const view =
      queryParameters.get(
        "view"
      ) ||
      "pendent";

    const prefillPickupPoints =
      getPrefillPickupPoints(
        prefill
      );

    const prefillDeliveryPoints =
      getPrefillDeliveryPoints(
        prefill
      );

    const [
      ready,
      setReady,
    ] =
      useState(
        Boolean(
          prefill
        )
      );

    const [
      loading,
      setLoading,
    ] =
      useState(
        false
      );

    const [
      error,
      setError,
    ] =
      useState(
        ""
      );

      const [
        successPopupOpen,
        setSuccessPopupOpen,
      ] =
        useState(
          false
        );
  
      const [
        savedEditSnapshot,
        setSavedEditSnapshot,
      ] =
        useState(
          null
        );
  
      const [
        initialLoadComplete,
        setInitialLoadComplete,
      ] =
        useState(
          false
        );
  
      const [
        unsavedChangesModalOpen,
        setUnsavedChangesModalOpen,
      ] =
        useState(
          false
        );
  
      const [
        pendingNavigation,
        setPendingNavigation,
      ] =
        useState(
          null
        );
  
      const latestEditSnapshotRef =
        useRef(
          ""
        );
  
      const historyGuardActiveRef =
        useRef(
          false
        );
  
      const historyGuardTokenRef =
        useRef(
          ""
        );
  
      const bypassHistoryGuardRef =
        useRef(
          false
        );
  
      const [
        currentPendentLoad,
        setCurrentPendentLoad,
      ] =
        useState(
          prefill ||
            null
        );

    const [
      priceNegotiation,
      setPriceNegotiation,
    ] =
      useState(
        () => {
          if (
            !isShipmentRequest ||
            !shipmentRequestId
          ) {
            return normalizeCarrierPriceNegotiation();
          }

          const cachedEntry =
            readShipmentPriceNegotiationCache(
              shipmentRequestId
            );

          return normalizeCarrierPriceNegotiation(
            cachedEntry
              ?.data ||
              {}
          );
        }
      );

    const [
      priceProposalAmount,
      setPriceProposalAmount,
    ] =
      useState(
        ""
      );

    const [
      priceProposalNote,
      setPriceProposalNote,
    ] =
      useState(
        ""
      );

    const [
      priceNegotiationLoading,
      setPriceNegotiationLoading,
    ] =
      useState(
        false
      );

    const [
      priceNegotiationSubmitting,
      setPriceNegotiationSubmitting,
    ] =
      useState(
        false
      );

    const [
      priceNegotiationError,
      setPriceNegotiationError,
    ] =
      useState(
        ""
      );

    const shipmentRequestRefreshTimerRef =
      useRef(
        null
      );

    const shipmentRequestRefreshAbortRef =
      useRef(
        null
      );

    const shipmentPriceRefreshAbortRef =
      useRef(
        null
      );

    const [
      companyName,
      setCompanyName,
    ] =
      useState(
        ""
      );

    const [
      pickupPoints,
      setPickupPoints,
    ] =
      useState(
        prefillPickupPoints.length >
          0
          ? prefillPickupPoints.map(
              normalizePoint
            )
          : [
              makeEmptyPoint(),
            ]
      );

    const [
      deliveryPoints,
      setDeliveryPoints,
    ] =
      useState(
        prefillDeliveryPoints.length >
          0
          ? prefillDeliveryPoints.map(
              normalizePoint
            )
          : [
              makeEmptyPoint(),
            ]
      );

    const [
      length,
      setLength,
    ] =
      useState(
        prefill?.length ===
          null ||
        prefill?.length ===
          undefined
          ? ""
          : String(
              prefill.length
            )
      );

    const [
      width,
      setWidth,
    ] =
      useState(
        prefill?.width ===
          null ||
        prefill?.width ===
          undefined
          ? ""
          : String(
              prefill.width
            )
      );

    const [
      height,
      setHeight,
    ] =
      useState(
        prefill?.height ===
          null ||
        prefill?.height ===
          undefined
          ? ""
          : String(
              prefill.height
            )
      );

    const [
      weight,
      setWeight,
    ] =
      useState(
        prefill?.weight ===
          null ||
        prefill?.weight ===
          undefined
          ? ""
          : String(
              prefill.weight
            )
      );

    const [
      price,
      setPrice,
    ] =
      useState(
        prefill?.price ===
          null ||
        prefill?.price ===
          undefined
          ? ""
          : String(
              prefill.price
            )
      );

    const [
      price4subcontracted,
      setPrice4subcontracted,
    ] =
      useState(
        prefill
          ?.price4subcontracted ===
          null ||
        prefill
          ?.price4subcontracted ===
          undefined
          ? ""
          : String(
              prefill
                .price4subcontracted
            )
      );

    const [
      merchandise,
      setMerchandise,
    ] =
      useState(
        prefill?.merchandise ||
          ""
      );

    const [
      notes4driver,
      setNotes4driver,
    ] =
      useState(
        prefill?.notes4driver ||
          ""
      );

    const [
      reference,
      setReference,
    ] =
      useState(
        prefill?.reference ===
          null ||
        prefill?.reference ===
          undefined
          ? ""
          : String(
              prefill.reference
            )
      );

    const [
      trailerType,
      setTrailerType,
    ] =
      useState(
        prefill?.trailerType ??
          null
      );

    const [
      trailerTypeOther,
      setTrailerTypeOther,
    ] =
      useState(
        prefill?.trailerTypeOther ||
          ""
      );

    const [
      clientType,
      setClientType,
    ] =
      useState(
        prefill?.industryClientId
          ? "industry"
          : prefill?.carrierClientId
            ? "carrier"
            : ""
      );

    const [
      selectedClient,
      setSelectedClient,
    ] =
      useState(
        prefill?.industryClientId
          ? {
              value:
                prefill.industryClientId,

              label:
                prefill.industryClientName ||
                prefill.industryClient
                  ?.companyName ||
                "",
            }
          : prefill?.carrierClientId
            ? {
                value:
                  prefill.carrierClientId,

                label:
                  prefill.carrierClientName ||
                  prefill.carrierClient
                    ?.companyName ||
                  "",
              }
            : null
      );

    const [
      transportCarrierId,
      setTransportCarrierId,
    ] =
      useState(
        prefill?.carrierId
          ? {
              value:
                String(
                  prefill.carrierId
                ),

              label:
                prefill.carrierName ||
                prefill.loadsAsCarrier
                  ?.companyName ||
                t(
                  "fallbacks.carrier"
                ),

              isInternal:
                false,
            }
          : prefill
            ? {
                value:
                  "__internal__",

                label:
                  prefill.carrierName ||
                  t(
                    "general.internalCarrier"
                  ),

                isInternal:
                  true,
              }
            : null
      );

    const [
      selectedClientContactEmails,
      setSelectedClientContactEmails,
    ] =
      useState(
        normalizeEmailOptions(
          prefill
            ?.clientContactEmails
        )
      );

    const [
      selectedSubcontractedEmails,
      setSelectedSubcontractedEmails,
    ] =
      useState(
        normalizeEmailOptions(
          prefill
            ?.subcontractedEmails
        )
      );

    const [
      industryOptions,
      setIndustryOptions,
    ] =
      useState(
        []
      );

    const [
      carrierClientOptions,
      setCarrierClientOptions,
    ] =
      useState(
        []
      );

      const [
        externalCarrierOptions,
        setExternalCarrierOptions,
      ] =
        useState(
          []
        );
  
      const [
        placeOptions,
        setPlaceOptions,
      ] =
        useState(
          []
        );
  
      const [
        merchandiseError,
        setMerchandiseError,
      ] =
        useState(
          ""
        );

    const [
      weightError,
      setWeightError,
    ] =
      useState(
        ""
      );

    const [
      lengthError,
      setLengthError,
    ] =
      useState(
        ""
      );

    const [
      widthError,
      setWidthError,
    ] =
      useState(
        ""
      );

    const [
      heightError,
      setHeightError,
    ] =
      useState(
        ""
      );

    const [
      priceError,
      setPriceError,
    ] =
      useState(
        ""
      );

    const [
      price4subcontractedError,
      setPrice4subcontractedError,
    ] =
      useState(
        ""
      );

    const [
      trailerTypeOtherError,
      setTrailerTypeOtherError,
    ] =
      useState(
        ""
      );

    const loadingAccessOptions =
      useMemo(
        () => [
          {
            value:
              "SIDE",

            label:
              t(
                "loadingAccess.options.side"
              ),
          },

          {
            value:
              "REAR",

            label:
              t(
                "loadingAccess.options.rear"
              ),
          },

          {
            value:
              "TOP",

            label:
              t(
                "loadingAccess.options.top"
              ),
          },

          {
            value:
              "DOCK",

            label:
              t(
                "loadingAccess.options.dock"
              ),
          },

          {
            value:
              "ANY",

            label:
              t(
                "loadingAccess.options.any"
              ),
          },
        ],
        [
          t,
        ]
      );

    const handlingEquipmentOptions =
      useMemo(
        () => [
          {
            value:
              "FORKLIFT",

            label:
              t(
                "handlingEquipment.options.forklift"
              ),
          },

          {
            value:
              "PALLET_JACK",

            label:
              t(
                "handlingEquipment.options.palletJack"
              ),
          },

          {
            value:
              "ELECTRIC_PALLET_JACK",

            label:
              t(
                "handlingEquipment.options.electricPalletJack"
              ),
          },

          {
            value:
              "CRANE",

            label:
              t(
                "handlingEquipment.options.crane"
              ),
          },

          {
            value:
              "LOADING_RAMP",

            label:
              t(
                "handlingEquipment.options.loadingRamp"
              ),
          },

          {
            value:
              "TAIL_LIFT",

            label:
              t(
                "handlingEquipment.options.tailLift"
              ),
          },

          {
            value:
              "HAND_LOAD",

            label:
              t(
                "handlingEquipment.options.handLoad"
              ),
          },

          {
            value:
              "CONVEYOR",

            label:
              t(
                "handlingEquipment.options.conveyor"
              ),
          },

          {
            value:
              "OTHER",

            label:
              t(
                "handlingEquipment.options.other"
              ),
          },
        ],
        [
          t,
        ]
      );

    const trailerTypeOptions =
      useMemo(
        () => [
          {
            value:
              "ANY",

            label:
              t(
                "trailerType.options.ANY"
              ),
          },

          {
            value:
              "TAUTLINER",

            label:
              t(
                "trailerType.options.TAUTLINER"
              ),
          },

          {
            value:
              "REEFER",

            label:
              t(
                "trailerType.options.REEFER"
              ),
          },

          {
            value:
              "BOX",

            label:
              t(
                "trailerType.options.BOX"
              ),
          },

          {
            value:
              "MEGA",

            label:
              t(
                "trailerType.options.MEGA"
              ),
          },

          {
            value:
              "FLATBED",

            label:
              t(
                "trailerType.options.FLATBED"
              ),
          },

          {
            value:
              "TIPPER",

            label:
              t(
                "trailerType.options.TIPPER"
              ),
          },

          {
            value:
              "LOWBED",

            label:
              t(
                "trailerType.options.LOWBED"
              ),
          },

          {
            value:
              "TANKER",

            label:
              t(
                "trailerType.options.TANKER"
              ),
          },

          {
            value:
              "CONTAINER_CHASSIS",

            label:
              t(
                "trailerType.options.CONTAINER_CHASSIS"
              ),
          },

          {
            value:
              "OTHER",

            label:
              t(
                "trailerType.options.OTHER"
              ),
          },
        ],
        [
          t,
        ]
      );

    const internalCarrierOption =
      useMemo(
        () => ({
          value:
            "__internal__",

          label:
            companyName
              ? t(
                  "general.internalCarrierWithCompany",
                  {
                    companyName,
                  }
                )
              : t(
                  "general.internalCarrier"
                ),

          isInternal:
            true,

          searchText:
            `${companyName} ${t(
              "general.internalCarrier"
            )}`
              .trim()
              .toLowerCase(),
        }),
        [
          companyName,
          t,
        ]
      );

    const availableCarrierOptions =
      useMemo(
        () => [
          internalCarrierOption,
          ...externalCarrierOptions,
        ],
        [
          externalCarrierOptions,
          internalCarrierOption,
        ]
      );

    const selectedTransportOption =
      useMemo(
        () =>
          availableCarrierOptions.find(
            (
              option
            ) =>
              String(
                option.value
              ) ===
              String(
                transportCarrierId?.value
              )
          ) ||
          transportCarrierId ||
          null,
        [
          availableCarrierOptions,
          transportCarrierId,
        ]
      );

    const isInternalCarrier =
      Boolean(
        selectedTransportOption
          ?.isInternal ||
        String(
          selectedTransportOption
            ?.value ||
            ""
        ) ===
          "__internal__"
      );

    const showPrice4subcontracted =
      Boolean(
        selectedTransportOption
          ?.value &&
        !isInternalCarrier
      );

    const priceProposals =
      priceNegotiation
        .proposals;

    const latestPriceProposal =
      priceNegotiation
        .latestProposal;

    const priceNegotiationActions =
      priceNegotiation
        .actions ||
      {};

    const latestPriceProposalByType =
      String(
        latestPriceProposal
          ?.proposedByType ||
          ""
      )
        .trim()
        .toUpperCase();

    const latestPriceProposalStatus =
      String(
        latestPriceProposal
          ?.status ||
          ""
      )
        .trim()
        .toUpperCase();

    const latestProposalIsPending =
      latestPriceProposalStatus ===
      "PENDING";

    const latestProposalIsFromShipper =
      latestPriceProposalByType ===
        "SHIPPER" ||
      latestPriceProposalByType.startsWith(
        "SHIPPER_"
      );

    const latestProposalIsFromCarrier =
      latestPriceProposalByType ===
        "CARRIER" ||
      latestPriceProposalByType.startsWith(
        "CARRIER_"
      );

    const hasPendingShipperProposal =
      Boolean(
        latestPriceProposal &&
        latestProposalIsPending &&
        latestProposalIsFromShipper
      );

    const hasPendingCarrierProposal =
      Boolean(
        latestPriceProposal &&
        latestProposalIsPending &&
        latestProposalIsFromCarrier
      );

    const canSendPriceProposal =
      Boolean(
        priceNegotiationActions
          .canCreateInitialProposal ||
        priceNegotiationActions
          .canCreateCounterProposal
      );

    const canAcceptPriceProposal =
      Boolean(
        priceNegotiationActions
          .canAccept ||
        hasPendingShipperProposal
      );

    const canRejectPriceProposal =
      Boolean(
        priceNegotiationActions
          .canReject ||
        hasPendingShipperProposal
      );

    const canWithdrawPriceProposal =
      Boolean(
        priceNegotiationActions
          .canWithdraw ||
        hasPendingCarrierProposal
      );

    const waitingForShipperPriceResponse =
      Boolean(
        priceNegotiationActions
          .waitingForShipper ||
        hasPendingCarrierProposal
      );

    const baseClientOptions =
      useMemo(
        () =>
          clientType ===
          "industry"
            ? industryOptions
            : clientType ===
                "carrier"
              ? carrierClientOptions
              : [],
        [
          carrierClientOptions,
          clientType,
          industryOptions,
        ]
      );

    const clientOptionsForWorkspace =
      useMemo(
        () => {
          if (
            !selectedClient
          ) {
            return baseClientOptions;
          }

          const selectedExists =
            baseClientOptions.some(
              (
                option
              ) =>
                String(
                  option.value
                ) ===
                String(
                  selectedClient.value
                )
            );

          return selectedExists
            ? baseClientOptions
            : [
                selectedClient,
                ...baseClientOptions,
              ];
        },
        [
          baseClientOptions,
          selectedClient,
        ]
      );

    const isSpecialLoad =
      Boolean(
        currentPendentLoad
          ?.specialLoad ??
        prefill?.specialLoad
      );

      const isLoadBearer =
      Boolean(
        currentPendentLoad
          ?.loadBearer ??
        prefill?.loadBearer
      );

    const currentEditSnapshot =
      useMemo(
        () => {
          const normalizeNumber =
            (
              value
            ) => {
              if (
                value ===
                  null ||
                value ===
                  undefined ||
                String(
                  value
                ).trim() ===
                  ""
              ) {
                return null;
              }

              const parsedValue =
                Number(
                  String(
                    value
                  ).replace(
                    ",",
                    "."
                  )
                );

              return Number.isFinite(
                parsedValue
              )
                ? parsedValue
                : null;
            };

          const normalizeText =
            (
              value
            ) =>
              String(
                value ??
                  ""
              ).trim();

          return JSON.stringify({
            pickupPoints:
              sanitizePoints(
                pickupPoints
              ),

            deliveryPoints:
              sanitizePoints(
                deliveryPoints
              ),

            length:
              normalizeNumber(
                length
              ),

            width:
              normalizeNumber(
                width
              ),

            height:
              normalizeNumber(
                height
              ),

            weight:
              normalizeNumber(
                weight
              ),

            merchandise:
              normalizeText(
                merchandise
              ),

            price:
              normalizeNumber(
                price
              ),

            price4subcontracted:
              showPrice4subcontracted
                ? normalizeNumber(
                    price4subcontracted
                  )
                : null,

            notes4driver:
              normalizeText(
                notes4driver
              ),

            reference:
              normalizeText(
                reference
              ),

            trailerType:
              String(
                trailerType ||
                  ""
              )
                .trim()
                .toUpperCase(),

            trailerTypeOther:
              normalizeText(
                trailerTypeOther
              ),

            clientType:
              clientType ||
              "",

            selectedClientId:
              selectedClient
                ?.value ??
              null,

            transportCarrier: {
              value:
                selectedTransportOption
                  ?.value ??
                null,

              isInternal:
                Boolean(
                  isInternalCarrier
                ),
            },
          });
        },
        [
          clientType,
          deliveryPoints,
          height,
          isInternalCarrier,
          length,
          merchandise,
          notes4driver,
          pickupPoints,
          price,
          price4subcontracted,
          reference,
          selectedClient,
          selectedTransportOption,
          showPrice4subcontracted,
          trailerType,
          trailerTypeOther,
          weight,
          width,
        ]
      );

    latestEditSnapshotRef.current =
      currentEditSnapshot;

    const hasUnsavedChanges =
      !isShipmentRequest &&
      initialLoadComplete &&
      savedEditSnapshot !==
        null &&
      currentEditSnapshot !==
        savedEditSnapshot;

    const pickupDimensionTotals =
      useMemo(
        () =>
          calculatePointDimensionTotals(
            pickupPoints
          ),
        [
          pickupPoints,
        ]
      );

    const deliveryDimensionTotals =
      useMemo(
        () =>
          calculatePointDimensionTotals(
            deliveryPoints
          ),
        [
          deliveryPoints,
        ]
      );

    const pointDimensionSummary =
      useMemo(
        () => ({
          length:
            pickupDimensionTotals.length,

          width:
            Math.max(
              pickupDimensionTotals.width,
              deliveryDimensionTotals.width
            ),

          height:
            Math.max(
              pickupDimensionTotals.height,
              deliveryDimensionTotals.height
            ),
        }),
        [
          deliveryDimensionTotals.height,
          deliveryDimensionTotals.width,
          pickupDimensionTotals.height,
          pickupDimensionTotals.length,
          pickupDimensionTotals.width,
        ]
      );

    const applyShipmentRequestSnapshot =
      useCallback(
        (
          snapshot
        ) => {
          if (
            !snapshot ||
            typeof snapshot !==
              "object"
          ) {
            return;
          }

          const nextPickupPoints =
            getPrefillPickupPoints(
              snapshot
            ).map(
              normalizePoint
            );

          const nextDeliveryPoints =
            getPrefillDeliveryPoints(
              snapshot
            ).map(
              normalizePoint
            );

          setCurrentPendentLoad(
            (
              currentValue
            ) => ({
              ...(currentValue ||
                {}),

              ...snapshot,
            })
          );

          setPickupPoints(
            nextPickupPoints.length >
              0
              ? nextPickupPoints
              : [
                  makeEmptyPoint(),
                ]
          );

          setDeliveryPoints(
            nextDeliveryPoints.length >
              0
              ? nextDeliveryPoints
              : [
                  makeEmptyPoint(),
                ]
          );

          setLength(
            snapshot.length ===
              null ||
            snapshot.length ===
              undefined
              ? ""
              : String(
                  snapshot.length
                )
          );

          setWidth(
            snapshot.width ===
              null ||
            snapshot.width ===
              undefined
              ? ""
              : String(
                  snapshot.width
                )
          );

          setHeight(
            snapshot.height ===
              null ||
            snapshot.height ===
              undefined
              ? ""
              : String(
                  snapshot.height
                )
          );

          setWeight(
            snapshot.weight ===
              null ||
            snapshot.weight ===
              undefined
              ? ""
              : String(
                  snapshot.weight
                )
          );

          const nextPrice =
            snapshot.price ??
            snapshot.transportPrice;

          setPrice(
            nextPrice ===
              null ||
            nextPrice ===
              undefined
              ? ""
              : String(
                  nextPrice
                )
          );

          setMerchandise(
            typeof snapshot
              .merchandise ===
            "string"
              ? snapshot
                  .merchandise
              : ""
          );

          setTrailerType(
            snapshot.trailerType ??
              null
          );

          setTrailerTypeOther(
            typeof snapshot
              .trailerTypeOther ===
            "string"
              ? snapshot
                  .trailerTypeOther
              : ""
          );

          const nextNotes =
            snapshot.notes4driver ??
            snapshot.notesForCarrier;

          setNotes4driver(
            typeof nextNotes ===
            "string"
              ? nextNotes
              : ""
          );

          const nextReference =
            snapshot.reference ??
            snapshot.loadReference;

          setReference(
            nextReference ===
              null ||
            nextReference ===
              undefined
              ? ""
              : String(
                  nextReference
                )
          );

          const nextClientId =
            snapshot
              .industryClientId ??
            snapshot
              .merchandiseClientId;

          const nextClientName =
            snapshot
              .industryClientName ||
            snapshot
              .merchandiseClient ||
            "";

          if (
            nextClientId
          ) {
            setClientType(
              "industry"
            );

            setSelectedClient({
              value:
                nextClientId,

              label:
                nextClientName,
            });
          } else {
            setClientType(
              ""
            );

            setSelectedClient(
              null
            );
          }

          setError(
            ""
          );

          setReady(
            true
          );
        },
        []
      );

    const loadShipmentRequestSnapshot =
      useCallback(
        async () => {
          if (
            !isShipmentRequest ||
            !shipmentRequestId
          ) {
            return null;
          }

          shipmentRequestRefreshAbortRef
            .current
            ?.abort();

          const controller =
            new AbortController();

          shipmentRequestRefreshAbortRef.current =
            controller;

          try {
            const response =
              await axios.get(
                `/carrier-request-conversations/${shipmentRequestId}/request`,
                {
                  signal:
                    controller.signal,

                  params: {
                    cacheBust:
                      Date.now(),
                  },
                }
              );

            const snapshot =
              response.data
                ?.request ||
              null;

            if (
              !snapshot
            ) {
              throw new Error(
                t(
                  "errors.shipmentRequestSnapshotMissing"
                )
              );
            }

            applyShipmentRequestSnapshot(
              snapshot
            );

            return snapshot;
          } catch (
            requestError
          ) {
            if (
              requestError?.code ===
                "ERR_CANCELED" ||
              controller.signal
                .aborted
            ) {
              return null;
            }

            console.error(
              "[EditPendentLoad] Failed to refresh shipment request:",
              requestError
            );

            const status =
              requestError
                ?.response
                ?.status;

            if (
              status ===
                404
            ) {
              setError(
                t(
                  "errors.shipmentRequestUnavailable"
                )
              );
            } else {
              setError(
                t(
                  "errors.shipmentRequestUpdateFailed"
                )
              );
            }

            setReady(
              true
            );

            return null;
          }
        },
        [
          applyShipmentRequestSnapshot,
          isShipmentRequest,
          shipmentRequestId,
          t,
        ]
      );

    const loadShipmentRequestPriceNegotiation =
      useCallback(
        async ({
          silent =
            false,

          preferCache =
            true,
        } = {}) => {
          if (
            !isShipmentRequest ||
            !shipmentRequestId
          ) {
            setPriceNegotiation(
              normalizeCarrierPriceNegotiation()
            );

            return null;
          }

          const cachedEntry =
            preferCache
              ? readShipmentPriceNegotiationCache(
                  shipmentRequestId
                )
              : null;

          if (
            cachedEntry
              ?.data
          ) {
            setPriceNegotiation(
              normalizeCarrierPriceNegotiation(
                cachedEntry.data
              )
            );

            setPriceNegotiationLoading(
              false
            );
          }

          shipmentPriceRefreshAbortRef
            .current
            ?.abort();

          const controller =
            new AbortController();

          shipmentPriceRefreshAbortRef.current =
            controller;

          if (
            !silent &&
            !cachedEntry
          ) {
            setPriceNegotiationLoading(
              true
            );
          }

          setPriceNegotiationError(
            ""
          );

          try {
            const response =
              await axios.get(
                `/pendentloads/shipment-requests/${shipmentRequestId}/price-proposals`,
                {
                  signal:
                    controller.signal,

                  params: {
                    cacheBust:
                      Date.now(),
                  },
                }
              );

            const responsePayload =
              response.data ||
              {};

            const normalizedNegotiation =
              normalizeCarrierPriceNegotiation(
                responsePayload
              );

            setPriceNegotiation(
              normalizedNegotiation
            );

            writeShipmentPriceNegotiationCache(
              shipmentRequestId,
              responsePayload
            );

            return normalizedNegotiation;
          } catch (
            requestError
          ) {
            if (
              requestError?.code ===
                "ERR_CANCELED" ||
              controller.signal
                .aborted
            ) {
              return null;
            }

            console.error(
              "[EditPendentLoad] Failed to load price negotiation:",
              requestError
            );

            if (
              !cachedEntry
            ) {
              setPriceNegotiationError(
                t(
                  "errors.negotiationLoadFailed"
                )
              );
            }

            return cachedEntry
              ? normalizeCarrierPriceNegotiation(
                  cachedEntry.data
                )
              : null;
          } finally {
            if (
              !controller.signal
                .aborted
            ) {
              setPriceNegotiationLoading(
                false
              );
            }
          }
        },
        [
          isShipmentRequest,
          shipmentRequestId,
          t,
        ]
      );

    useEffect(
      () => {
        if (
          !isShipmentRequest ||
          !shipmentRequestId
        ) {
          return undefined;
        }

        const cachedEntry =
          readShipmentPriceNegotiationCache(
            shipmentRequestId
          );

        if (
          cachedEntry
            ?.data
        ) {
          setPriceNegotiation(
            normalizeCarrierPriceNegotiation(
              cachedEntry.data
            )
          );
        }

        return subscribeShipmentPriceNegotiationCache(
          shipmentRequestId,
          (
            nextEntry
          ) => {
            if (
              !nextEntry
                ?.data
            ) {
              return;
            }

            setPriceNegotiation(
              normalizeCarrierPriceNegotiation(
                nextEntry.data
              )
            );
          }
        );
      },
      [
        isShipmentRequest,
        shipmentRequestId,
      ]
    );

    const runPriceNegotiationAction =
      useCallback(
        async ({
          action =
            "",
          payload = {},
        } = {}) => {
          if (
            !isShipmentRequest ||
            !shipmentRequestId ||
            priceNegotiationSubmitting
          ) {
            return false;
          }

          setPriceNegotiationSubmitting(
            true
          );

          setPriceNegotiationError(
            ""
          );

          try {
            const proposalActions =
              new Set([
                "accept",
                "reject",
                "withdraw",
              ]);

            const isProposalAction =
              proposalActions.has(
                action
              );

            let negotiationForAction =
              priceNegotiation;

            let proposalId =
              Number(
                negotiationForAction
                  ?.latestProposal
                  ?.id ||
                negotiationForAction
                  ?.conversation
                  ?.latestPriceProposalId ||
                negotiationForAction
                  ?.conversation
                  ?.latestPriceProposal
                  ?.id
              );

            if (
              isProposalAction &&
              (
                !Number.isInteger(
                  proposalId
                ) ||
                proposalId <=
                  0
              )
            ) {
              const proposalResponse =
                await axios.get(
                  `/pendentloads/shipment-requests/${shipmentRequestId}/price-proposals`,
                  {
                    params: {
                      cacheBust:
                        Date.now(),
                    },
                  }
                );

              const proposalPayload =
                proposalResponse
                  ?.data ||
                {};

              const refreshedNegotiation =
                normalizeCarrierPriceNegotiation(
                  proposalPayload
                );

              negotiationForAction =
                refreshedNegotiation;

              setPriceNegotiation(
                refreshedNegotiation
              );

              writeShipmentPriceNegotiationCache(
                shipmentRequestId,
                proposalPayload
              );

              const rawProposals =
                Array.isArray(
                  proposalPayload
                    ?.proposals
                )
                  ? proposalPayload.proposals
                  : [];

              const rawLatestProposalId =
                Number(
                  proposalPayload
                    ?.conversation
                    ?.latestPriceProposalId
                );

              const rawLatestProposal =
                proposalPayload
                  ?.conversation
                  ?.latestPriceProposal ||
                rawProposals.find(
                  (
                    proposal
                  ) =>
                    Number(
                      proposal
                        ?.id
                    ) ===
                    rawLatestProposalId
                ) ||
                rawProposals[
                  rawProposals.length -
                    1
                ] ||
                null;

              proposalId =
                Number(
                  rawLatestProposalId ||
                  rawLatestProposal
                    ?.id ||
                  refreshedNegotiation
                    ?.latestProposal
                    ?.id
                );

              console.log(
                "[EditPendentLoad] Proposal resolved for action:",
                {
                  shipmentRequestId,

                  action,

                  proposalId,

                  latestPriceProposalId:
                    proposalPayload
                      ?.conversation
                      ?.latestPriceProposalId ||
                    null,

                  latestPriceProposal:
                    proposalPayload
                      ?.conversation
                      ?.latestPriceProposal ||
                    null,

                  proposals:
                    rawProposals,
                }
              );
            }

            if (
              isProposalAction &&
              (
                !Number.isInteger(
                  proposalId
                ) ||
                proposalId <=
                  0
              )
            ) {
              console.error(
                "[EditPendentLoad] Could not resolve latest price proposal.",
                {
                  shipmentRequestId,

                  action,

                  negotiation:
                    negotiationForAction,
                }
              );

              throw new Error(
                t(
                  "errors.negotiationProposalResolveFailed"
                )
              );
            }

            const endpoint =
              isProposalAction
                ? `/pendentloads/shipment-requests/${shipmentRequestId}/price-proposals/${proposalId}/${action}`
                : `/pendentloads/shipment-requests/${shipmentRequestId}/price-proposals`;

            const response =
              isProposalAction
                ? await axios.patch(
                    endpoint,
                    payload
                  )
                : await axios.post(
                    endpoint,
                    payload
                  );

            if (
              action ===
              "accept"
            ) {
              return (
                response?.data ||
                true
              );
            }

            await Promise.all([
              loadShipmentRequestPriceNegotiation({
                silent:
                  true,

                preferCache:
                  false,
              }),

              loadShipmentRequestSnapshot(),
            ]);

            return (
              response?.data ||
              true
            );
          } catch (
            requestError
          ) {
            console.error(
              `[EditPendentLoad] Price negotiation action "${action || "create"}" failed:`,
              {
                status:
                  requestError
                    ?.response
                    ?.status,

                data:
                  requestError
                    ?.response
                    ?.data,

                method:
                  requestError
                    ?.config
                    ?.method,

                url:
                  requestError
                    ?.config
                    ?.url,

                error:
                  requestError,
              }
            );

            setPriceNegotiationError(
              t(
                "errors.negotiationUpdateFailed"
              )
            );

            return false;
          } finally {
            setPriceNegotiationSubmitting(
              false
            );
          }
        },
        [
          isShipmentRequest,
          loadShipmentRequestPriceNegotiation,
          loadShipmentRequestSnapshot,
          priceNegotiation,
          priceNegotiationSubmitting,
          shipmentRequestId,
          t,
        ]
      );

    const handleSendPriceProposal =
      useCallback(
        async () => {
          const normalizedAmount =
            String(
              priceProposalAmount ??
                ""
            )
              .trim()
              .replace(
                ",",
                "."
              );

          const numericAmount =
            Number(
              normalizedAmount
            );

          if (
            !Number.isFinite(
              numericAmount
            ) ||
            numericAmount <=
              0
          ) {
            setPriceNegotiationError(
              t(
                "errors.invalidProposalAmount"
              )
            );

            return false;
          }

          const proposalSent =
            await runPriceNegotiationAction({
              payload: {
                amount:
                  Number(
                    numericAmount.toFixed(
                      2
                    )
                  ),

                currency:
                  latestPriceProposal
                    ?.currency ||
                  "EUR",

                note:
                  String(
                    priceProposalNote ||
                      ""
                  ).trim() ||
                  null,
              },
            });

          if (
            proposalSent
          ) {
            setPriceProposalAmount(
              ""
            );

            setPriceProposalNote(
              ""
            );
          }

          return proposalSent;
        },
        [
          latestPriceProposal
            ?.currency,
          priceProposalAmount,
          priceProposalNote,
          runPriceNegotiationAction,
          t,
        ]
      );

    const handleAcceptPriceProposal =
      useCallback(
        async () => {
          const acceptanceResult =
            await runPriceNegotiationAction({
              action:
                "accept",
            });

          if (
            !acceptanceResult
          ) {
            return false;
          }

          const acceptedLoadId =
            Number(
              acceptanceResult
                ?.load
                ?.id
            );

          if (
            !Number.isInteger(
              acceptedLoadId
            ) ||
            acceptedLoadId <=
              0
          ) {
            console.error(
              "[EditPendentLoad] Proposal accepted but backend did not return a valid Current Load ID:",
              acceptanceResult
            );

            navigate(
              "/loads?view=current",
              {
                replace:
                  true,
              }
            );

            return true;
          }

          navigate(
            `/loads?view=current&focusLoadId=${acceptedLoadId}`,
            {
              replace:
                true,
            }
          );

          return true;
        },
        [
          navigate,
          runPriceNegotiationAction,
        ]
      );

    const handleRejectPriceProposal =
      useCallback(
        () =>
          runPriceNegotiationAction({
            action:
              "reject",
          }),
        [
          runPriceNegotiationAction,
        ]
      );

    const handleWithdrawPriceProposal =
      useCallback(
        () =>
          runPriceNegotiationAction({
            action:
              "withdraw",
          }),
        [
          runPriceNegotiationAction,
        ]
      );

    useEffect(
      () => {
        let cancelled =
          false;

        const fetchCompanyName =
          async () => {
            try {
              const response =
                await axios.get(
                  "/auth/whoami"
                );

              if (
                cancelled
              ) {
                return;
              }

              setCompanyName(
                String(
                  response.data
                    ?.company_name ||
                    ""
                ).trim()
              );
            } catch (
              requestError
            ) {
              console.warn(
                "❌ Error fetching company name:",
                requestError
                  ?.response
                  ?.status
              );

              if (
                !cancelled
              ) {
                setCompanyName(
                  ""
                );
              }
            }
          };

        fetchCompanyName();

        return () => {
          cancelled =
            true;
        };
      },
      []
    );

    useEffect(
      () => {
        let cancelled =
          false;

          const fetchLists =
          async () => {
            const [
              industriesResult,
              carrierClientsResult,
              placesResult,
            ] =
              await Promise.allSettled([
                axios.get(
                  "/industries"
                ),

                axios.get(
                  "/carriers"
                ),

                axios.get(
                  "/places"
                ),
              ]);

            if (
              cancelled
            ) {
              return;
            }

            if (
              industriesResult.status ===
              "fulfilled"
            ) {
              const nextIndustryOptions =
                (
                  Array.isArray(
                    industriesResult
                      .value
                      .data
                  )
                    ? industriesResult
                        .value
                        .data
                    : []
                )
                  .map(
                    (
                      industry
                    ) => ({
                      value:
                        industry.id,

                      label:
                        industry.companyName ||
                        industry.name ||
                        "",

                      raw:
                        industry,
                    })
                  )
                  .filter(
                    (
                      option
                    ) =>
                      option.label
                  )
                  .sort(
                    (
                      optionA,
                      optionB
                    ) =>
                      optionA.label.localeCompare(
                        optionB.label,
                        undefined,
                        {
                          sensitivity:
                            "base",
                        }
                      )
                  );

              setIndustryOptions(
                nextIndustryOptions
              );
            }

            if (
              carrierClientsResult.status ===
              "fulfilled"
            ) {
              const carriers =
                Array.isArray(
                  carrierClientsResult
                    .value
                    .data
                )
                  ? carrierClientsResult
                      .value
                      .data
                  : [];

              const nextCarrierClientOptions =
                carriers
                  .map(
                    (
                      carrier
                    ) => {
                      const carrierName =
                        String(
                          carrier
                            ?.companyName ||
                            carrier
                              ?.name ||
                            carrier
                              ?.company
                              ?.companyName ||
                            carrier
                              ?.company
                              ?.name ||
                            ""
                        ).trim();

                      return {
                        value:
                          carrier.id,

                        label:
                          carrierName,

                        raw:
                          carrier,
                      };
                    }
                  )
                  .filter(
                    (
                      option
                    ) =>
                      option.label
                  )
                  .sort(
                    (
                      optionA,
                      optionB
                    ) =>
                      optionA.label.localeCompare(
                        optionB.label,
                        undefined,
                        {
                          sensitivity:
                            "base",
                        }
                      )
                  );

              const nextExternalCarrierOptions =
                nextCarrierClientOptions.map(
                  (
                    option
                  ) => ({
                    ...option,

                    value:
                      String(
                        option.value
                      ),

                    isInternal:
                      false,

                    searchText:
                      option.label.toLowerCase(),
                  })
                );

                setCarrierClientOptions(
                  nextCarrierClientOptions
                );
  
                setExternalCarrierOptions(
                  nextExternalCarrierOptions
                );
              }
  
              if (
                placesResult.status ===
                "fulfilled"
              ) {
                const places =
                  Array.isArray(
                    placesResult
                      .value
                      .data
                  )
                    ? placesResult
                        .value
                        .data
                    : [];
  
                const nextPlaceOptions =
                  places
                    .map(
                      (
                        place
                      ) => ({
                        value:
                          place.id,
  
                        label:
                          place.placeName ||
                          place.name ||
                          "",
  
                        metaCity:
                          place.city ||
                          "",
  
                        metaCountry:
                          place.country ||
                          "",
  
                        fullData:
                          place,
                      })
                    )
                    .filter(
                      (
                        option
                      ) =>
                        option.label
                    )
                    .sort(
                      (
                        optionA,
                        optionB
                      ) =>
                        optionA.label.localeCompare(
                          optionB.label,
                          undefined,
                          {
                            sensitivity:
                              "base",
                          }
                        )
                    );
  
                setPlaceOptions(
                  nextPlaceOptions
                );
              }
            };
  
          fetchLists().catch(
          (
            requestError
          ) => {
            console.error(
              "❌ Error loading clients and carriers:",
              requestError
            );
          }
        );

        return () => {
          cancelled =
            true;
        };
      },
      []
    );

    useEffect(
      () => {
        let cancelled =
          false;

        if (
          isShipmentRequest
        ) {
          if (
            prefill
          ) {
            applyShipmentRequestSnapshot(
              prefill
            );
          } else {
            setReady(
              false
            );
          }

          void Promise.all([
            loadShipmentRequestSnapshot(),

            loadShipmentRequestPriceNegotiation({
              silent:
                Boolean(
                  readShipmentPriceNegotiationCache(
                    shipmentRequestId
                  )
                ),

              preferCache:
                true,
            }),
          ]);

          return () => {
            cancelled =
              true;
          };
        }

        setInitialLoadComplete(
          false
        );

        setSavedEditSnapshot(
          null
        );

        const fetchLoad =
          async () => {
            try {
              const response =
                await axios.get(
                  `/pendentloads/${id}`
                );

              if (
                cancelled
              ) {
                return;
              }

              const data =
                response.data ||
                {};

              setCurrentPendentLoad(
                data
              );

              setLength(
                data.length ===
                  null ||
                data.length ===
                  undefined
                  ? ""
                  : String(
                      data.length
                    )
              );

              setWidth(
                data.width ===
                  null ||
                data.width ===
                  undefined
                  ? ""
                  : String(
                      data.width
                    )
              );

              setHeight(
                data.height ===
                  null ||
                data.height ===
                  undefined
                  ? ""
                  : String(
                      data.height
                    )
              );

              setWeight(
                data.weight ===
                  null ||
                data.weight ===
                  undefined
                  ? ""
                  : String(
                      data.weight
                    )
              );

              setPrice(
                data.price ===
                  null ||
                data.price ===
                  undefined
                  ? ""
                  : String(
                      data.price
                    )
              );

              setPrice4subcontracted(
                data.price4subcontracted ===
                  null ||
                data.price4subcontracted ===
                  undefined
                  ? ""
                  : String(
                      data.price4subcontracted
                    )
              );

              setMerchandise(
                typeof data.merchandise ===
                "string"
                  ? data.merchandise
                  : ""
              );

              setNotes4driver(
                typeof data.notes4driver ===
                "string"
                  ? data.notes4driver
                  : ""
              );

              setReference(
                data.reference ===
                  null ||
                data.reference ===
                  undefined
                  ? ""
                  : String(
                      data.reference
                    )
              );

              setTrailerType(
                data.trailerType ??
                null
              );

              setTrailerTypeOther(
                typeof data.trailerTypeOther ===
                "string"
                  ? data.trailerTypeOther
                  : ""
              );

              const apiPickupPoints =
                (
                  Array.isArray(
                    data.pickupDeliveries
                  )
                    ? data.pickupDeliveries
                    : Array.isArray(
                          data.pickupPoints
                        )
                      ? data.pickupPoints
                      : []
                ).map(
                  normalizePoint
                );

              const apiDeliveryPoints =
                (
                  Array.isArray(
                    data.deliveryDeliveries
                  )
                    ? data.deliveryDeliveries
                    : Array.isArray(
                          data.deliveryPoints
                        )
                      ? data.deliveryPoints
                      : []
                ).map(
                  normalizePoint
                );

              setPickupPoints(
                apiPickupPoints.length >
                  0
                  ? apiPickupPoints
                  : [
                      makeEmptyPoint(),
                    ]
              );

              setDeliveryPoints(
                apiDeliveryPoints.length >
                  0
                  ? apiDeliveryPoints
                  : [
                      makeEmptyPoint(),
                    ]
              );

              if (
                data.industryClientId
              ) {
                setClientType(
                  "industry"
                );

                setSelectedClient({
                  value:
                    data.industryClientId,

                  label:
                    data.industryClientName ||
                    data.industryClient
                      ?.companyName ||
                    "",
                });
              } else if (
                data.carrierClientId
              ) {
                setClientType(
                  "carrier"
                );

                setSelectedClient({
                  value:
                    data.carrierClientId,

                  label:
                    data.carrierClientName ||
                    data.carrierClient
                      ?.companyName ||
                    "",
                });
              } else {
                setClientType(
                  ""
                );

                setSelectedClient(
                  null
                );
              }

              setSelectedClientContactEmails(
                normalizeEmailOptions(
                  data.clientContactEmails
                )
              );

              setSelectedSubcontractedEmails(
                normalizeEmailOptions(
                  data.subcontractedEmails
                )
              );

              if (
                data.carrierId
              ) {
                setTransportCarrierId({
                  value:
                    String(
                      data.carrierId
                    ),

                  label:
                    data.carrierName ||
                    data.loadsAsCarrier
                      ?.companyName ||
                    t(
                      "fallbacks.carrier"
                    ),

                  isInternal:
                    false,
                });
              } else {
                setTransportCarrierId({
                  value:
                    "__internal__",

                  label:
                    data.carrierName ||
                    t(
                      "general.internalCarrier"
                    ),

                  isInternal:
                    true,
                });
              }

              setError(
                ""
              );
            } catch (
              requestError
            ) {
              console.error(
                "❌ Failed to fetch pendent load:",
                requestError
              );

              setError(
                t(
                  "errors.fetchFailed"
                )
              );
            } finally {
              if (
                !cancelled
              ) {
                setReady(
                  true
                );

                setInitialLoadComplete(
                  true
                );
              }
            }
          };

        fetchLoad();

        return () => {
          cancelled =
            true;
        };
      },
      [
        applyShipmentRequestSnapshot,
        id,
        isShipmentRequest,
        loadShipmentRequestPriceNegotiation,
        loadShipmentRequestSnapshot,
        prefill,
        t,
      ]
    );

    useEffect(
      () => {
        if (
          !initialLoadComplete ||
          isShipmentRequest ||
          savedEditSnapshot !==
            null
        ) {
          return undefined;
        }

        const timeoutId =
          window.setTimeout(
            () => {
              setSavedEditSnapshot(
                latestEditSnapshotRef.current
              );
            },
            0
          );

        return () => {
          window.clearTimeout(
            timeoutId
          );
        };
      },
      [
        initialLoadComplete,
        isShipmentRequest,
        savedEditSnapshot,
      ]
    );

    useEffect(
      () => {
        if (
          !hasUnsavedChanges
        ) {
          return undefined;
        }

        const handleBeforeUnload =
          (
            event
          ) => {
            event.preventDefault();

            event.returnValue =
              "";
          };

        window.addEventListener(
          "beforeunload",
          handleBeforeUnload
        );

        return () => {
          window.removeEventListener(
            "beforeunload",
            handleBeforeUnload
          );
        };
      },
      [
        hasUnsavedChanges,
      ]
    );

    useEffect(
      () => {
        if (
          !hasUnsavedChanges
        ) {
          return undefined;
        }

        const handleDocumentClick =
          (
            event
          ) => {
            if (
              event.defaultPrevented ||
              event.button !==
                0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }

            const target =
              event.target;

            const anchor =
              target?.closest
                ? target.closest(
                    "a[href]"
                  )
                : null;

            if (
              !anchor
            ) {
              return;
            }

            if (
              anchor.target ===
                "_blank" ||
              anchor.hasAttribute(
                "download"
              )
            ) {
              return;
            }

            const rawHref =
              anchor.getAttribute(
                "href"
              );

            if (
              !rawHref ||
              rawHref.startsWith(
                "#"
              )
            ) {
              return;
            }

            let targetUrl;

            try {
              targetUrl =
                new URL(
                  anchor.href,
                  window.location.href
                );
            } catch {
              return;
            }

            const currentUrl =
              new URL(
                window.location.href
              );

            if (
              targetUrl.href ===
              currentUrl.href
            ) {
              return;
            }

            event.preventDefault();

            event.stopPropagation();

            setPendingNavigation({
              type:
                targetUrl.origin ===
                currentUrl.origin
                  ? "internal"
                  : "external",

              href:
                targetUrl.href,
            });

            setUnsavedChangesModalOpen(
              true
            );
          };

        document.addEventListener(
          "click",
          handleDocumentClick,
          true
        );

        return () => {
          document.removeEventListener(
            "click",
            handleDocumentClick,
            true
          );
        };
      },
      [
        hasUnsavedChanges,
      ]
    );

    useEffect(
      () => {
        if (
          String(
            transportCarrierId
              ?.value ||
              ""
          ) !==
          "__internal__"
        ) {
          return;
        }

        setTransportCarrierId(
          internalCarrierOption
        );
      },
      [
        internalCarrierOption,
        transportCarrierId?.value,
      ]
    );

    useEffect(
      () => {
        if (
          showPrice4subcontracted
        ) {
          return;
        }

        setPrice4subcontracted(
          ""
        );

        setPrice4subcontractedError(
          ""
        );

        setSelectedSubcontractedEmails(
          []
        );
      },
      [
        showPrice4subcontracted,
      ]
    );

    useEffect(
      () => {
        if (
          pointDimensionSummary.length >
          0
        ) {
          setLength(
            pointDimensionSummary.length.toFixed(
              2
            )
          );

          setLengthError(
            ""
          );
        }

        if (
          pointDimensionSummary.width >
          0
        ) {
          setWidth(
            pointDimensionSummary.width.toFixed(
              2
            )
          );

          setWidthError(
            ""
          );
        }

        if (
          pointDimensionSummary.height >
          0
        ) {
          setHeight(
            pointDimensionSummary.height.toFixed(
              2
            )
          );

          setHeightError(
            ""
          );
        }
      },
      [
        pointDimensionSummary.height,
        pointDimensionSummary.length,
        pointDimensionSummary.width,
      ]
    );

    useEffect(
      () => {
        if (
          !isShipmentRequest ||
          !shipmentRequestId
        ) {
          return undefined;
        }

        const socket =
          getSocket();

        const scheduleRefresh =
          (
            realtimeEvent = {}
          ) => {
            if (
              Number(
                realtimeEvent
                  ?.requestId
              ) !==
              shipmentRequestId
            ) {
              return;
            }

            const changeType =
              String(
                realtimeEvent
                  ?.changeType ||
                  ""
              )
                .trim()
                .toUpperCase();

            if (
              changeType ===
                "MESSAGE_CREATED" ||
              changeType ===
                "MESSAGES_CHANGED"
            ) {
              return;
            }

            if (
              shipmentRequestRefreshTimerRef
                .current
            ) {
              window.clearTimeout(
                shipmentRequestRefreshTimerRef
                  .current
              );
            }

            shipmentRequestRefreshTimerRef.current =
              window.setTimeout(
                () => {
                  shipmentRequestRefreshTimerRef.current =
                    null;

                  void Promise.all([
                    loadShipmentRequestSnapshot(),

                    loadShipmentRequestPriceNegotiation({
                      silent:
                        true,

                      preferCache:
                        false,
                    }),
                  ]);
                },
                180
              );
          };

        const handleConnect =
          () => {
            void Promise.all([
              loadShipmentRequestSnapshot(),

              loadShipmentRequestPriceNegotiation({
                silent:
                  true,

                preferCache:
                  false,
              }),
            ]);
          };

        socket.on(
          "shipmentrequest:changed",
          scheduleRefresh
        );

        socket.on(
          "connect",
          handleConnect
        );

        if (
          !socket.connected &&
          typeof socket.connect ===
            "function"
        ) {
          socket.connect();
        }

        return () => {
          socket.off(
            "shipmentrequest:changed",
            scheduleRefresh
          );

          socket.off(
            "connect",
            handleConnect
          );

          if (
            shipmentRequestRefreshTimerRef
              .current
          ) {
            window.clearTimeout(
              shipmentRequestRefreshTimerRef
                .current
            );

            shipmentRequestRefreshTimerRef.current =
              null;
          }

          shipmentRequestRefreshAbortRef
            .current
            ?.abort();

          shipmentRequestRefreshAbortRef.current =
            null;
        };
      },
      [
        isShipmentRequest,
        loadShipmentRequestPriceNegotiation,
        loadShipmentRequestSnapshot,
        shipmentRequestId,
      ]
    );

    const getDimensionLimits =
      (
        specialLoad
      ) => {
        if (
          specialLoad
        ) {
          return {
            length: {
              min:
                0.01,

              max:
                SPECIAL_MAX.length,

              message:
                t(
                  "errors.specialLengthMax"
                ),
            },

            width: {
              min:
                0.01,

              max:
                SPECIAL_MAX.width,

              message:
                t(
                  "errors.specialWidthMax"
                ),
            },

            height: {
              min:
                0.01,

              max:
                SPECIAL_MAX.height,

              message:
                t(
                  "errors.specialHeightMax"
                ),
            },
          };
        }

        return {
          length: {
            min:
              0.01,

            max:
              NORMAL_MAX.length,

            message:
              t(
                "errors.lengthMax"
              ),
          },

          width: {
            min:
              0.01,

            max:
              NORMAL_MAX.width,

            message:
              t(
                "errors.widthMax"
              ),
          },

          height: {
            min:
              0.01,

            max:
              NORMAL_MAX.height,

            message:
              t(
                "errors.heightMax"
              ),
          },
        };
      };

    const validateDimension =
      (
        field,
        value
      ) => {
        if (
          value ===
            null ||
          value ===
            undefined ||
          String(
            value
          ).trim() ===
            ""
        ) {
          return "";
        }

        const numericValue =
          Number(
            value
          );

        if (
          !Number.isFinite(
            numericValue
          ) ||
          numericValue <=
            0
        ) {
          return t(
            "errors.positiveNumber"
          );
        }

        const limits =
          getDimensionLimits(
            isSpecialLoad
          )[
            field
          ];

        if (
          numericValue >
          limits.max
        ) {
          return limits.message;
        }

        return "";
      };

    const validatePoint =
      (
        point
      ) => {
        const nextPoint = {
          ...point,

          locationError:
            !String(
              point.location ||
                ""
            ).trim(),

          dateError:
            false,

          hourError:
            false,

          addressError:
            false,

          postalCodeError:
            false,

          cityError:
            false,

          countryError:
            false,

          gMapsLinkError:
            false,

          coordinatesError:
            false,

          contactPhoneError:
            false,

          loadingAccessError:
            false,

          handlingEquipmentError:
            false,
        };

        [
          "length",
          "width",
          "height",
        ].forEach(
          (
            field
          ) => {
            const dimensionError =
              validateDimension(
                field,
                point[
                  field
                ]
              );

            nextPoint[
              `${field}Error`
            ] =
              dimensionError;

            nextPoint[
              `${field}ErrorFlag`
            ] =
              Boolean(
                dimensionError
              );
          }
        );

        const needsOtherEquipment =
          String(
            point.handlingEquipment ||
              ""
          )
            .trim()
            .toUpperCase() ===
          "OTHER";

        nextPoint.handlingEquipmentOtherError =
          needsOtherEquipment &&
          !String(
            point.handlingEquipmentOther ||
              ""
          ).trim();

        return nextPoint;
      };

    const removePickUpPlace =
      (
        index
      ) => {
        setPickupPoints(
          (
            currentPoints
          ) =>
            currentPoints.filter(
              (
                _,
                pointIndex
              ) =>
                pointIndex !==
                index
            )
        );
      };

    const removeDeliveryPlace =
      (
        index
      ) => {
        setDeliveryPoints(
          (
            currentPoints
          ) =>
            currentPoints.filter(
              (
                _,
                pointIndex
              ) =>
                pointIndex !==
                index
            )
        );
      };

    const handleUpdate =
      async (
        event,
        overrides = {}
      ) => {
        event?.preventDefault?.();

        const notes4driverValue =
          Object.prototype.hasOwnProperty.call(
            overrides,
            "notes4driver"
          )
            ? overrides.notes4driver
            : notes4driver;

        const suppressSuccessPopup =
          Boolean(
            overrides.suppressSuccessPopup
          );

        const pickupPointsForSave =
          Object.prototype.hasOwnProperty.call(
            overrides,
            "pickupPoints"
          ) &&
          Array.isArray(
            overrides.pickupPoints
          )
            ? overrides.pickupPoints
            : pickupPoints;

        const deliveryPointsForSave =
          Object.prototype.hasOwnProperty.call(
            overrides,
            "deliveryPoints"
          ) &&
          Array.isArray(
            overrides.deliveryPoints
          )
            ? overrides.deliveryPoints
            : deliveryPoints;

        if (
          isShipmentRequest
        ) {
          setError(
            t(
              "errors.shipmentRequestReadOnly"
            )
          );

          return false;
        }

        setError(
          ""
        );

        setMerchandiseError(
          ""
        );

        setWeightError(
          ""
        );

        setLengthError(
          ""
        );

        setWidthError(
          ""
        );

        setHeightError(
          ""
        );

        setPriceError(
          ""
        );

        setPrice4subcontractedError(
          ""
        );

        setTrailerTypeOtherError(
          ""
        );

        let hasValidationError =
          false;

        let validationMessage =
          "";

        const hasLength =
          String(
            length ??
              ""
          ).trim() !==
          "";

        const hasWidth =
          String(
            width ??
              ""
          ).trim() !==
          "";

        const hasHeight =
          String(
            height ??
              ""
          ).trim() !==
          "";

        const hasWeight =
          String(
            weight ??
              ""
          ).trim() !==
          "";

        const parsedLength =
          hasLength
            ? Number(
                length
              )
            : null;

        const parsedWidth =
          hasWidth
            ? Number(
                width
              )
            : null;

        const parsedHeight =
          hasHeight
            ? Number(
                height
              )
            : null;

        const parsedWeight =
          hasWeight
            ? Number(
                weight
              )
            : null;

        const nextLengthError =
          validateDimension(
            "length",
            length
          );

        const nextWidthError =
          validateDimension(
            "width",
            width
          );

        const nextHeightError =
          validateDimension(
            "height",
            height
          );

        const nextWeightError =
          hasWeight &&
          (
            !Number.isFinite(
              parsedWeight
            ) ||
            parsedWeight <=
              0
          )
            ? t(
                "errors.weightMustBePositive"
              )
            : "";

        setLengthError(
          nextLengthError
        );

        setWidthError(
          nextWidthError
        );

        setHeightError(
          nextHeightError
        );

        setWeightError(
          nextWeightError
        );

        if (
          nextLengthError ||
          nextWidthError ||
          nextHeightError ||
          nextWeightError
        ) {
          hasValidationError =
            true;
        }

        const hasAnyDimension =
          hasLength ||
          hasWidth ||
          hasHeight;

        if (
          isSpecialLoad &&
          hasAnyDimension
        ) {
          const hasSpecialDimension =
            (
              Number.isFinite(
                parsedLength
              ) &&
              parsedLength >=
                SPECIAL_THRESHOLD.length
            ) ||
            (
              Number.isFinite(
                parsedWidth
              ) &&
              parsedWidth >=
                SPECIAL_THRESHOLD.width
            ) ||
            (
              Number.isFinite(
                parsedHeight
              ) &&
              parsedHeight >=
                SPECIAL_THRESHOLD.height
            );

          if (
            !hasSpecialDimension
          ) {
            hasValidationError =
              true;

            validationMessage =
              t(
                "errors.specialNeedsOneDimension"
              );
          }
        }

        const merchandiseValue =
          String(
            merchandise ||
              ""
          ).trim();

        const referenceValue =
          String(
            reference ||
              ""
          ).trim();

        if (
          !merchandiseValue
        ) {
          const nextMerchandiseError =
            t(
              "errors.merchandiseRequired"
            );

          setMerchandiseError(
            nextMerchandiseError
          );

          hasValidationError =
            true;
        } else if (
          merchandiseValue.length >
          5000
        ) {
          const nextMerchandiseError =
            t(
              "errors.merchandiseTooLong"
            );

          setMerchandiseError(
            nextMerchandiseError
          );

          hasValidationError =
            true;
        }

        let parsedPrice =
          null;

        if (
          String(
            price ??
              ""
          ).trim()
        ) {
          const numericPrice =
            Number(
              price
            );

          if (
            !Number.isFinite(
              numericPrice
            ) ||
            numericPrice <
              0
          ) {
            setPriceError(
              t(
                "errors.invalidPrice"
              )
            );

            hasValidationError =
              true;
          } else {
            parsedPrice =
              Number(
                numericPrice.toFixed(
                  2
                )
              );
          }
        }

        let parsedSubcontractedPrice =
          null;

        if (
          showPrice4subcontracted &&
          String(
            price4subcontracted ??
              ""
          ).trim()
        ) {
          const numericSubcontractedPrice =
            Number(
              price4subcontracted
            );

          if (
            !Number.isFinite(
              numericSubcontractedPrice
            ) ||
            numericSubcontractedPrice <
              0
          ) {
            setPrice4subcontractedError(
              t(
                "errors.invalidPrice"
              )
            );

            hasValidationError =
              true;
          } else {
            parsedSubcontractedPrice =
              Number(
                numericSubcontractedPrice.toFixed(
                  2
                )
              );
          }
        }

        const normalizedTrailerType =
          String(
            trailerType ||
              ""
          )
            .trim()
            .toUpperCase();

        const nextTrailerTypeOtherError =
          normalizedTrailerType ===
            "OTHER" &&
          !String(
            trailerTypeOther ||
              ""
          ).trim()
            ? t(
                "errors.trailerTypeOtherRequired"
              )
            : "";

        setTrailerTypeOtherError(
          nextTrailerTypeOtherError
        );

        if (
          nextTrailerTypeOtherError
        ) {
          hasValidationError =
            true;
        }

        const validatedPickupPoints =
          pickupPointsForSave.map(
            validatePoint
          );

        const validatedDeliveryPoints =
          deliveryPointsForSave.map(
            validatePoint
          );

        setPickupPoints(
          validatedPickupPoints
        );

        setDeliveryPoints(
          validatedDeliveryPoints
        );

        const hasInvalidPickupPoint =
          validatedPickupPoints.some(
            (
              point
            ) =>
              point.locationError ||
              point.lengthErrorFlag ||
              point.widthErrorFlag ||
              point.heightErrorFlag ||
              point.handlingEquipmentOtherError
          );

        const hasInvalidDeliveryPoint =
          validatedDeliveryPoints.some(
            (
              point
            ) =>
              point.locationError ||
              point.lengthErrorFlag ||
              point.widthErrorFlag ||
              point.heightErrorFlag ||
              point.handlingEquipmentOtherError
          );

        if (
          hasInvalidPickupPoint ||
          hasInvalidDeliveryPoint
        ) {
          hasValidationError =
            true;

          if (
            !validationMessage
          ) {
            validationMessage =
              t(
                "errors.invalidStopFields"
              );
          }
        }

        const hasInvalidHour =
          [
            ...validatedPickupPoints,
            ...validatedDeliveryPoints,
          ].some(
            (
              point
            ) =>
              point.hour &&
              !/^([01]\d|2[0-3]):([0-5]\d)$/.test(
                String(
                  point.hour
                ).trim()
              )
          );

        if (
          hasInvalidHour
        ) {
          hasValidationError =
            true;

          if (
            !validationMessage
          ) {
            validationMessage =
              t(
                "errors.hourInvalid"
              );
          }
        }

        if (
          hasValidationError
        ) {
          setError(
            validationMessage ||
            t(
              "errors.correctMarkedFields"
            )
          );

          return false;
        }

        const hasTransportCarrier =
          Boolean(
            String(
              selectedTransportOption
                ?.value ||
                ""
            ).trim()
          );

        const payload = {
          length:
            parsedLength,

          width:
            parsedWidth,

          height:
            parsedHeight,

          weight:
            parsedWeight,

          price:
            parsedPrice,

          price4subcontracted:
            parsedSubcontractedPrice,

          merchandise:
            merchandiseValue.slice(
              0,
              5000
            ),

          notes4driver:
            String(
              notes4driverValue ||
                ""
            ).trim() ||
            null,

          reference:
            referenceValue ||
            null,

          industryClientId:
            clientType ===
            "industry"
              ? selectedClient
                  ?.value ??
                null
              : null,

          carrierClientId:
            clientType ===
            "carrier"
              ? selectedClient
                  ?.value ??
                null
              : null,

          clientContactEmails:
            selectedClientContactEmails.map(
              (
                option
              ) =>
                option.value
            ),

          subcontractedEmails:
            !hasTransportCarrier ||
            isInternalCarrier
              ? []
              : selectedSubcontractedEmails.map(
                  (
                    option
                  ) =>
                    option.value
                ),

          carrierId:
            !hasTransportCarrier ||
            isInternalCarrier
              ? null
              : Number(
                  selectedTransportOption
                    .value
                ),

          carrierName:
            !hasTransportCarrier
              ? null
              : isInternalCarrier
                ? companyName ||
                  selectedTransportOption
                    ?.label ||
                  null
                : selectedTransportOption
                    ?.label ||
                  null,

          trailerType:
            normalizedTrailerType ||
            null,

          trailerTypeOther:
            normalizedTrailerType ===
            "OTHER"
              ? String(
                  trailerTypeOther ||
                    ""
                ).trim() ||
                null
              : null,

          loadBearer:
            isLoadBearer,

          specialLoad:
            isSpecialLoad,

          pickupPoints:
            sanitizePoints(
              validatedPickupPoints
            ),

          deliveryPoints:
            sanitizePoints(
              validatedDeliveryPoints
            ),
        };

        try {
          setLoading(
            true
          );

          const response =
            await axios.put(
              `/pendentloads/${id}`,
              payload
            );

          if (
            response.status !==
            200
          ) {
            setError(
              t(
                "errors.updateFailed"
              )
            );

            return false;
          }

          setNotes4driver(
            String(
              notes4driverValue ||
                ""
            )
          );

          setReference(
            referenceValue
          );

          setCurrentPendentLoad(
            (
              currentValue
            ) => ({
              ...currentValue,

              ...payload,

              ...(
                response.data &&
                typeof response.data ===
                  "object"
                  ? response.data
                  : {}
              ),
            })
          );

          setSavedEditSnapshot(
            latestEditSnapshotRef.current
          );

          if (
            !suppressSuccessPopup
          ) {
            setSuccessPopupOpen(
              true
            );
          }

          return true;
        } catch (
          requestError
        ) {
          const responseStatus =
            requestError
              ?.response
              ?.status;

          const responseData =
            requestError
              ?.response
              ?.data;

          console.error(
            "❌ Error updating pendent load:",
            responseStatus,
            responseData,
            requestError
          );

          setError(
            t(
              "errors.updateFailed"
            )
          );

          return false;
        } finally {
          setLoading(
            false
          );
        }
      };

    const handleSaveNote =
      async (
        nextNotes
      ) => {
        setNotes4driver(
          nextNotes
        );

        return handleUpdate(
          null,
          {
            notes4driver:
              nextNotes,

            suppressSuccessPopup:
              true,
          }
        );
      };

      const handleCloseSuccessPopup =
      () => {
        setSuccessPopupOpen(
          false
        );

        bypassHistoryGuardRef.current =
          true;

        historyGuardActiveRef.current =
          false;

        navigate(
          `/loads?view=${view}`,
          {
            replace:
              true,
          }
        );
      };

    const requestNavigation =
      (
        to,
        options = {}
      ) => {
        if (
          !hasUnsavedChanges
        ) {
          navigate(
            to,
            options
          );

          return;
        }

        setPendingNavigation({
          type:
            "navigate",

          to,

          options,
        });

        setUnsavedChangesModalOpen(
          true
        );
      };

    const handleContinueEditing =
      () => {
        setUnsavedChangesModalOpen(
          false
        );

        setPendingNavigation(
          null
        );
      };

    const performPendingNavigation =
      (
        navigation
      ) => {
        if (
          !navigation
        ) {
          return;
        }

        bypassHistoryGuardRef.current =
          true;

        historyGuardActiveRef.current =
          false;

        if (
          navigation.type ===
          "navigate"
        ) {
          navigate(
            navigation.to,
            navigation.options ||
              {}
          );

          return;
        }

        if (
          navigation.type ===
          "internal"
        ) {
          const targetUrl =
            new URL(
              navigation.href
            );

          navigate(
            `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
          );

          return;
        }

        if (
          navigation.type ===
          "external"
        ) {
          window.location.assign(
            navigation.href
          );
        }
      };

    const handleLeaveWithoutSaving =
      () => {
        const navigation =
          pendingNavigation;

        setUnsavedChangesModalOpen(
          false
        );

        setPendingNavigation(
          null
        );

        performPendingNavigation(
          navigation
        );
      };

    const handleSaveAndLeave =
      async () => {
        const navigation =
          pendingNavigation;

        const saved =
          await handleUpdate(
            null
          );

        if (
          !saved
        ) {
          return;
        }

        setUnsavedChangesModalOpen(
          false
        );

        setSuccessPopupOpen(
          false
        );

        setPendingNavigation(
          null
        );

        performPendingNavigation(
          navigation
        );
      };

    const handleBack =
      () => {
        requestNavigation(
          `/loads?view=${view}`,
          {
            replace:
              true,
          }
        );
      };

    return (
      <>
      <PendentLoadWorkspace
        ready={
          ready
        }
        loading={
          loading
        }
        error={
          error
        }
        successPopupOpen={
          successPopupOpen
        }
        onCloseSuccessPopup={
          handleCloseSuccessPopup
        }
        onBack={
          handleBack
        }
        onSave={
          handleUpdate
        }
        onSaveNote={
          handleSaveNote
        }
        id={
          id
        }
        isShipmentRequest={
          isShipmentRequest
        }
        requestId={
          isShipmentRequest
            ? shipmentRequestId
            : (
                currentPendentLoad
                  ?.requestId ||
                currentPendentLoad
                  ?.sourceRequestId ||
                currentPendentLoad
                  ?.shipperRequestId ||
                null
              )
        }
        prefill={
          prefill
        }
        currentPendentLoad={
          currentPendentLoad
        }
        pickupPoints={
          pickupPoints
        }
        setPickupPoints={
          setPickupPoints
        }
        deliveryPoints={
          deliveryPoints
        }
        setDeliveryPoints={
          setDeliveryPoints
        }
        makeEmptyPoint={
          makeEmptyPoint
        }
        removePickUpPlace={
          removePickUpPlace
        }
        removeDeliveryPlace={
          removeDeliveryPlace
        }
        maxPoints={
          MAX_POINTS
        }
        merchandise={
          merchandise
        }
        setMerchandise={
          setMerchandise
        }
        weight={
          weight
        }
        setWeight={
          setWeight
        }
        length={
          length
        }
        setLength={
          setLength
        }
        width={
          width
        }
        setWidth={
          setWidth
        }
        height={
          height
        }
        setHeight={
          setHeight
        }
        trailerType={
          trailerType
        }
        setTrailerType={
          setTrailerType
        }
        trailerTypeOther={
          trailerTypeOther
        }
        setTrailerTypeOther={
          setTrailerTypeOther
        }
        trailerTypeOptions={
          trailerTypeOptions
        }
        loadingAccessOptions={
          loadingAccessOptions
        }
        handlingEquipmentOptions={
          handlingEquipmentOptions
        }
        price={
          price
        }
        setPrice={
          setPrice
        }
        price4subcontracted={
          price4subcontracted
        }
        setPrice4subcontracted={
          setPrice4subcontracted
        }
        showPrice4subcontracted={
          showPrice4subcontracted
        }
        clientType={
          clientType
        }
        setClientType={(
          nextClientType
        ) => {
          setClientType(
            nextClientType
          );

          setSelectedClient(
            null
          );

          setSelectedClientContactEmails(
            []
          );
        }}
        selectedClient={
          selectedClient
        }
        setSelectedClient={(
          nextClient
        ) => {
          setSelectedClient(
            nextClient
          );

          setSelectedClientContactEmails(
            []
          );
        }}
        clientOptions={
          clientOptionsForWorkspace
        }
        transportCarrierId={
          selectedTransportOption
        }
        setTransportCarrierId={(
          nextCarrier
        ) => {
          setTransportCarrierId(
            nextCarrier ||
            internalCarrierOption
          );

          setSelectedSubcontractedEmails(
            []
          );

          setPrice4subcontractedError(
            ""
          );
        }}
        carrierOptions={
          availableCarrierOptions
        }
        reference={
          reference
        }
        setReference={
          setReference
        }
        notes4driver={
          notes4driver
        }
        setNotes4driver={
          setNotes4driver
        }
        merchandiseError={
          merchandiseError
        }
        weightError={
          weightError
        }
        priceError={
          priceError
        }
        price4subcontractedError={
          price4subcontractedError
        }
        trailerTypeOtherError={
          trailerTypeOtherError
        }
        lengthError={
          lengthError
        }
        widthError={
          widthError
        }
        heightError={
          heightError
        }

        priceNegotiation={
          priceNegotiation
        }
        priceProposals={
          priceProposals
        }
        latestPriceProposal={
          latestPriceProposal
        }
        priceNegotiationActions={
          priceNegotiationActions
        }
        priceNegotiationLoading={
          priceNegotiationLoading
        }
        priceNegotiationSubmitting={
          priceNegotiationSubmitting
        }
        priceNegotiationError={
          priceNegotiationError
        }
        priceProposalAmount={
          priceProposalAmount
        }
        setPriceProposalAmount={
          setPriceProposalAmount
        }
        priceProposalNote={
          priceProposalNote
        }
        setPriceProposalNote={
          setPriceProposalNote
        }
        canSendPriceProposal={
          canSendPriceProposal
        }
        canAcceptPriceProposal={
          canAcceptPriceProposal
        }
        canRejectPriceProposal={
          canRejectPriceProposal
        }
        canWithdrawPriceProposal={
          canWithdrawPriceProposal
        }
        waitingForShipperPriceResponse={
          waitingForShipperPriceResponse
        }
        onSendPriceProposal={
          handleSendPriceProposal
        }
        onAcceptPriceProposal={
          handleAcceptPriceProposal
        }
        onRejectPriceProposal={
          handleRejectPriceProposal
        }
        onWithdrawPriceProposal={
          handleWithdrawPriceProposal
        }
      />

        {unsavedChangesModalOpen && (
          <div
            className="current-load-unsaved-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                handleContinueEditing();
              }
            }}
          >
            <div
              className="current-load-unsaved-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pendent-load-unsaved-title"
            >
              <div className="current-load-unsaved-modal__icon">
                !
              </div>

              <div className="current-load-unsaved-modal__content">
                <h2 id="pendent-load-unsaved-title">
                  {t(
                    "edit_load:workspace.unsavedChanges.title"
                  )}
                </h2>

                <p>
                  {t(
                    "edit_load:workspace.unsavedChanges.message"
                  )}
                </p>
              </div>

              <div className="current-load-unsaved-modal__actions">
                <button
                  type="button"
                  className="current-load-unsaved-modal__continue"
                  onClick={
                    handleContinueEditing
                  }
                >
                  {t(
                    "edit_load:workspace.unsavedChanges.continueEditing"
                  )}
                </button>

                <button
                  type="button"
                  className="current-load-unsaved-modal__save"
                  onClick={
                    handleSaveAndLeave
                  }
                  disabled={
                    loading
                  }
                >
                  {loading
                    ? t(
                        "edit_load:common.saving"
                      )
                    : t(
                        "edit_load:workspace.unsavedChanges.saveAndLeave"
                      )}
                </button>

                <button
                  type="button"
                  className="current-load-unsaved-modal__leave"
                  onClick={
                    handleLeaveWithoutSaving
                  }
                  disabled={
                    loading
                  }
                >
                  {t(
                    "edit_load:workspace.unsavedChanges.leaveWithoutSaving"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

export default EditPendentLoad;